import { skipToken } from "@reduxjs/toolkit/query";
import { TeammateType } from "@repo/app-types/entities/teammate";
import { asGradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { UserMessageForm } from "@repo/ui/chat/userMessageForm";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import {
  IconPlayerPlay,
  IconMessages as OutlineChatBubbleLeftRightIcon,
  IconMessages as SolidChatBubbleLeftRightIcon,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { Button as AriaButton, VisuallyHidden } from "react-aria-components";
import { useLocation, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  usePlaybookGradingCallOutlineQuery,
  usePlaybookGradingQuery,
} from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { CallActivityEmptyState } from "./callActivityEmptyState.js";
import { CallOutlineNavigationPopover } from "./callOutlineNavigationPopover.js";
import {
  parseTranscriptSearchNavState,
  useSortedRecordings,
} from "./gradingHelpers.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "./gradingTranscriptWithIndex.js";
import { useLiveCoachingPreviewWindow } from "./liveCoachingPreview/liveCoachingPreviewWindow.js";
import { TranscriptSearchBar } from "./snippetSharing/transcriptSearchBar.js";
import { useTranscriptSearchNavigation } from "./snippetSharing/useTranscriptSearchNavigation.js";
import { TeammateAccountRecordingsSummary } from "./teammateAccountRecordingsSummary.js";
import { TeamPerformanceCopilot } from "./teamPerformanceCopilot.js";
import { TranscriptVideoPanel } from "./transcriptVideoPanel.js";
import { useTeamPerformanceCopilot } from "./useTeamPerformanceCopilot.js";
import { useTranscriptOutlineSync } from "./useTranscriptOutlineSync.js";
import { VoiceAgentCallFeedbackWidget } from "./voiceAgentCallFeedbackWidget.js";

/**
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/customer-interactions/<gradingId>/transcript
 * or https://<host>/agents/<teammateAgentId>/customer-interactions/<gradingId>/transcript
 */
export const TrainingTeammatePageOverviewGradingTranscript: FC = () => {
  const { gradingId: gradingIdParam, teammateAgentId } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });

  // teammate is derived from the grading; the route only carries the grading id.
  const { data: teammate } = useBasicTeammateQuery(
    grading?.teammate_id ?? skipToken,
  );

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<GradingTranscriptWithIndexHandle>(null);

  const sortedRecordings = useSortedRecordings(grading?.recordings);

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });
  const [hasScrolledTranscript, setHasScrolledTranscript] =
    useState<boolean>(false);

  // Show the summary sidebar if a templated recording summary is available
  const recordingSummary =
    grading?.assessment?.templated_summary?.trim() || null;

  const [isCopilotExpanded, setIsCopilotExpanded] = useState(false);

  const copilot = useTeamPerformanceCopilot({
    gradingId: grading?.id,
    sortedRecordings,
  });

  const searchNavState = parseTranscriptSearchNavState(useLocation().state);
  const searchNav = useTranscriptSearchNavigation({
    recordings: sortedRecordings,
    navState: searchNavState,
    transcriptRef,
    transcriptContainerRef,
  });

  const onQuotePress = useCallback((quoteId: string) => {
    const currentTranscriptContainer = transcriptContainerRef.current;
    const currentTranscript = transcriptRef.current;
    if (!currentTranscriptContainer || !currentTranscript) {
      return;
    }

    currentTranscript.focusQuote({
      container: currentTranscriptContainer,
      quoteId,
      highlightQuote: true,
    });
  }, []);

  // Toggle header bottom border only when transcript area is scrolled
  useEffect(() => {
    const container = transcriptContainerRef.current;
    if (!container) return;

    const handleScroll = (): void => {
      setHasScrolledTranscript(container.scrollTop > 0);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [transcriptContainerRef]);

  const hasLiveCoachingPreview = useIsFeatureFlagEnabled(
    FeatureFlagKey.LiveCoachingPreview,
  );

  const { liveCoachingPreviewWindow, onLiveCoachingPreviewOpen } =
    useLiveCoachingPreviewWindow({
      gradingId,
      sortedRecordings,
    });

  return (
    <>
      <div
        className={twMerge(
          "grid h-full grid-rows-[minmax(0,1fr)_auto_auto] overflow-hidden",
          recordingSummary
            ? "grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto]"
            : "grid-cols-[minmax(0,1fr)_auto]",
        )}
      >
        {recordingSummary ? (
          <div className="relative space-y-4 overflow-y-auto border-r border-border-light bg-background">
            <TeammateAccountRecordingsSummary
              summary={recordingSummary}
              isLoading={isGradingLoading}
            />
          </div>
        ) : null}

        <div className="flex flex-col bg-white">
          <div className="space-y-2 border-b border-border-light p-4">
            <div className="flex justify-between gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  Call transcript
                </h3>
                {teammate?.type === TeammateType.AGENT ? (
                  <VoiceAgentCallFeedbackWidget
                    gradingId={grading?.id}
                    teammateAgentId={teammateAgentId}
                    existingFeedback={
                      grading?.feedback?.voice_agent_call ?? null
                    }
                  />
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                {searchNavState && searchNav.count > 0 ? (
                  <TranscriptSearchBar
                    query={searchNavState.query}
                    count={searchNav.count}
                    currentMatchIndex={searchNav.currentMatchIndex}
                    onPrev={searchNav.prev}
                    onNext={searchNav.next}
                    onClose={searchNav.close}
                  />
                ) : null}
                {hasLiveCoachingPreview ? (
                  <Button
                    size={Size.MEDIUM}
                    variant={ButtonVariant.Tertiary}
                    icon={<IconPlayerPlay className="size-4 shrink-0" />}
                    onClick={onLiveCoachingPreviewOpen}
                  >
                    Preview Alpharun Assist
                  </Button>
                ) : null}
              </div>
            </div>

            <CallOutlineNavigationPopover
              callOutline={callOutline}
              isLoading={isCallOutlineLoading}
              isError={isCallOutlineError}
              callPosition={callPosition}
              onCallPositionRequest={scrollToCallPosition}
            />
          </div>
          <TranscriptVideoPanel recordings={sortedRecordings} />
          <div
            ref={transcriptContainerRef}
            // `flex-1` so the empty state below can centre itself in the panel
            // rather than in a box the height of its own text.
            className="relative flex flex-1 flex-col gap-4 overflow-y-auto p-4"
          >
            {sortedRecordings.length === 0 ? (
              <CallActivityEmptyState
                callActivity={grading?.call_activity}
                surface="transcript"
              />
            ) : null}

            <GradingTranscriptWithIndex
              ref={transcriptRef}
              recordings={sortedRecordings}
              quoteMatches={copilot.quoteMatches}
              searchMatches={searchNav.searchMatches}
              scrollToCallPosition={scrollToCallPosition}
              toolActivitiesByRecordingId={toolActivitiesByRecordingId}
            />
          </div>
        </div>

        <div>
          {isCopilotExpanded ? (
            <div className="h-full w-[400px] shrink-0 border-l border-border-light bg-background p-4">
              <div className="flex h-full flex-col">
                <div className="sticky top-0 z-10 bg-background pb-2">
                  <Label size={Size.MEDIUM}>Assistant</Label>
                </div>
                <div className="min-h-0 flex-1 ">
                  <div className="flex h-full flex-col">
                    {grading?.assessment?.recording_assessment_template_id ? (
                      <TeamPerformanceCopilot
                        visibleMessages={copilot.visibleMessages}
                        quoteMatches={copilot.quoteMatches}
                        isLoading={copilot.isLoading}
                        composerValue={copilot.composerValue}
                        onComposerValueChange={copilot.setComposerValue}
                        onMessageSend={copilot.onMessageSend}
                        onMessageRetry={copilot.onMessageRetry}
                        sortedRecordings={sortedRecordings}
                        onQuotePress={onQuotePress}
                        onCollapse={() => setIsCopilotExpanded(false)}
                      />
                    ) : (
                      <div className="flex items-center justify-center p-4 text-muted-foreground">
                        {isGradingLoading
                          ? "Loading..."
                          : "No assessment available"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="col-span-full">
          {!isCopilotExpanded ? (
            <div className="flex items-end gap-2 border-t border-border-light bg-background px-4 py-2.5">
              <UserMessageForm
                className="flex-auto"
                isDisabled={copilot.isLoading}
                placeholder={
                  copilot.visibleMessages.length > 0
                    ? "Ask more about this transcript..."
                    : "Ask anything about this transcript..."
                }
                value={copilot.composerValue}
                onValueChange={copilot.setComposerValue}
                onMessageSend={(message) => {
                  copilot.onMessageSend(message);
                  setIsCopilotExpanded(true);
                }}
              />

              <AriaButton
                className="-mr-1 size-10 p-2.5"
                onPress={() => setIsCopilotExpanded(true)}
              >
                {copilot.visibleMessages.length > 0 ? (
                  <SolidChatBubbleLeftRightIcon className="size-5 text-indigo-600 hover:text-indigo-700" />
                ) : (
                  <OutlineChatBubbleLeftRightIcon className="size-5 text-muted-foreground hover:text-gray-600" />
                )}

                <VisuallyHidden>Expand assistant</VisuallyHidden>
              </AriaButton>
            </div>
          ) : null}
        </div>

        <div className="col-span-full">
          <AudioPlayer />
        </div>
      </div>

      {liveCoachingPreviewWindow}
    </>
  );
};
